import { useState, useRef, useEffect } from 'react'
import './MultiSelect.css'

interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export default function MultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  className = ''
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleOptionToggle = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value]
    onChange(newValues)
  }

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([])
    } else {
      onChange(options.map(opt => opt.value))
    }
  }

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0])
      return option ? option.label : selectedValues[0]
    }
    return `${selectedValues.length} selected`
  }

  return (
    <div className={`multi-select ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className={`multi-select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className="multi-select-text">{getDisplayText()}</span>
        <span className="multi-select-arrow">▼</span>
      </button>

      {isOpen && (
        <div className="multi-select-dropdown">
          {options.length > 1 && (
            <div className="multi-select-option select-all">
              <label>
                <input
                  type="checkbox"
                  checked={selectedValues.length === options.length}
                  onChange={handleSelectAll}
                />
                <span className="option-label">
                  {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
                </span>
              </label>
            </div>
          )}
          {options.length > 0 ? (
            options.map((option) => (
              <div key={option.value} className="multi-select-option">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleOptionToggle(option.value)}
                  />
                  <span className="option-label">{option.label}</span>
                </label>
              </div>
            ))
          ) : (
            <div className="multi-select-empty">No options available</div>
          )}
        </div>
      )}
    </div>
  )
}
